import React, { useEffect, useState } from "react";
import {
  Collections,
  Delete,
  Edit,
  Image,
  Info,
  Mic,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";

import { QuestionApiServiceImpl } from "app/services/Ananse/impl/QuestionApiService";
import { selectInput } from "../../../../../../mocks/quizInputs";
import { useTranslation } from "_metronic/i18n/language";
import useQuestionsListModel from "./hooks/useQuestionsListModel";
import UtilityBar from "./compoments/UtilityBar";

import { CollectionType, FileType, QuestionType, QuizType } from "app/types";
import { QUIZ_TYPES_CONTANTS } from "app/types/quiz-constants";
import { getAudiometryCategory } from "../../quiznew/quiznew-view";
import {
  DecriptionTooltip,
  MediaTooltip,
  ModalViewImagesAnswers,
  TableCellButtons,
  TableHeader,
} from "./components";

type QuestionsTableProps = {
  collections: CollectionType[];
  questions: QuestionType[];
  quiz: QuizType;
  onAdd: () => void;
  onDelete: (questionId: number) => void;
  onEdit: (question: QuestionType) => void;
  onClone?: (question: QuestionType) => void;
  onRefresh: () => void;
  getCategoryCognitiveBy: (id: number) => string;
};

const questionService = new QuestionApiServiceImpl();

const QuestionsTable: React.FC<QuestionsTableProps> = ({
  collections,
  questions,
  quiz,
  onAdd,
  onDelete,
  onEdit,
  onClone,
  onRefresh,
  getCategoryCognitiveBy,
}) => {
  const translate = useTranslation();
  const [filteredQuestions, setFilteredQuestions] = useState<QuestionType[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof QuestionType>(
    "arrangeQuestionSequence",
  );
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalImages, setModalImages] = useState<FileType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");

  const { getFragranceById } = useQuestionsListModel();

  useEffect(() => {
    if (!searchTerm) {
      setFilteredQuestions(questions);
      return;
    }

    const lowerCasedSearchTerm = searchTerm.toLowerCase();

    const filtered = questions.filter(({ rightAnswer, quest, question }) => {
      const normalizedRightAnswer = rightAnswer
        ? rightAnswer.toLowerCase()
        : "";
      const normalizedQuest = quest ? quest.toLowerCase() : "";
      const normalizedQuestionQuest = question?.quest
        ? question.quest.toLowerCase()
        : "";

      return (
        normalizedRightAnswer.includes(lowerCasedSearchTerm) ||
        normalizedQuest.includes(lowerCasedSearchTerm) ||
        normalizedQuestionQuest.includes(lowerCasedSearchTerm)
      );
    });

    setFilteredQuestions(filtered);
  }, [searchTerm, questions]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSortRequest = (property: keyof QuestionType) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const extractNumber = (str: string): number | null => {
    const match = str.match(/^\d+/);
    return match ? parseInt(match[0], 10) : null;
  };

  const sortedQuestions = filteredQuestions.sort((a, b) => {
    const aValue: any =
      orderBy === "quest" && a?.question ? a.question[orderBy] : a[orderBy];
    const bValue: any =
      orderBy === "quest" && b?.question ? b.question[orderBy] : b[orderBy];

    if (typeof aValue === "string" && typeof bValue === "string") {
      const aNum = extractNumber(aValue);
      const bNum = extractNumber(bValue);

      if (aNum !== null && bNum !== null) {
        return order === "asc" ? aNum - bNum : bNum - aNum;
      } else if (aNum !== null && bNum === null) {
        return order === "asc" ? -1 : 1;
      } else if (aNum === null && bNum !== null) {
        return order === "asc" ? 1 : -1;
      }
    }

    if (aValue === undefined) return 1;
    if (bValue === undefined) return -1;

    if (order === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getQuestionType = (value: string) => {
    const findType = selectInput.find(v => v.type === value);
    return findType?.name || "N/A";
  };

  const handleMouseEnter = async (question: QuestionType) => {
    setLoading(true);
    setModalTitle(question?.question?.quest || "");
    setOpenModal(true);
    const images: FileType[] = [];

    for (const answer of question.answers) {
      const response = await questionService.getFileByFileOwnerId(
        answer.quizQuestionAnswerId.toString(),
      );
      const imageData = response[0];
      if (imageData && imageData.tempUri) {
        images.push(imageData);
      }
    }

    setModalImages(images);
    setLoading(false);
  };

  const handleMouseLeave = () => {
    setOpenModal(false);
    setModalImages([]);
    setModalTitle("");
  };

  return (
    <Paper>
      <UtilityBar
        clearTerm={() => setSearchTerm("")}
        searchTerm={searchTerm}
        handleSearch={handleSearch}
        translate={translate}
        onAdd={onAdd}
        onRefresh={onRefresh}
        sortedQuestions={sortedQuestions}
        quiz={quiz}
      />
      <TableContainer>
        <Table>
          <TableHeader
            translate={translate}
            quiz={quiz}
            order={order}
            orderBy={orderBy}
            QUIZ_TYPES_CONTANTS={QUIZ_TYPES_CONTANTS}
            handleSortRequest={handleSortRequest}
          />
          <TableBody>
            {sortedQuestions
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(question => (
                <TableRow key={question.questionId}>
                  <TableCellButtons
                    question={question}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    translate={translate}
                    onClone={onClone}
                  />
                  <TableCell>
                    {question.arrangeQuestionSequence || "-"}
                  </TableCell>
                  <TableCell>
                    <DecriptionTooltip
                      title={question?.question?.description}
                    />
                    <MediaTooltip
                      question={question}
                      quiz={quiz}
                      QUIZ_TYPES_CONTANTS={QUIZ_TYPES_CONTANTS}
                    />
                    {question?.question?.quest || "-"}
                  </TableCell>
                  <TableCell>
                    {question.answers.map(answer => (
                      <Typography key={answer.answerId}>
                        {question.type === "4"
                          ? `${answer.answer?.label} / ${getFragranceById(
                              collections,
                              Number(answer.answer?.description),
                            )}`
                          : answer.answer?.label}
                      </Typography>
                    ))}
                    {(question.type === "image" ||
                      +quiz.type === QUIZ_TYPES_CONTANTS.AUDIOMETRY) && (
                      <IconButton
                        title="Visualizar imagens"
                        onClick={() => handleMouseEnter(question)}
                      >
                        <Collections />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{question.rightAnswer}</TableCell>
                  <TableCell>
                    {+quiz.type !== QUIZ_TYPES_CONTANTS.AUDIOMETRY
                      ? +quiz.type === QUIZ_TYPES_CONTANTS.COGNITIVE
                        ? getCategoryCognitiveBy(
                            Number(question?.categoryCognitiveQuestionId),
                          )
                        : getFragranceById(collections, +question.fragranceId)
                      : getAudiometryCategory(
                          +question.categoryCognitiveQuestionId!,
                        )}
                  </TableCell>
                  <TableCell>{getQuestionType(question.type)}</TableCell>
                  <TableCell>
                    {question.createdAt
                      ? new Date(question?.createdAt).toLocaleDateString(
                          "pt-BR",
                          {
                            day: "numeric",
                            month: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                          },
                        )
                      : ""}
                  </TableCell>
                  <TableCell>
                    {question.updatedAt
                      ? new Date(question.updatedAt).toLocaleDateString(
                          "pt-BR",
                          {
                            day: "numeric",
                            month: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                          },
                        )
                      : ""}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredQuestions.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <ModalViewImagesAnswers
        open={openModal}
        handleClose={() => handleMouseLeave()}
        images={modalImages}
        loading={loading}
        title={modalTitle}
        translate={translate}
      />
    </Paper>
  );
};

export default QuestionsTable;
